<script setup>
import { CsCollapse } from '../collapse';
import { CsIcon } from '../icon';
import { CsText } from '../text';
import { computed, ref } from 'vue';
import { aiActivityRecipe } from './ai-activity.recipe';

/**
 * @typedef {import('./ai-activity.recipe').AiActivityRecipeVariants} AiActivityRecipeVariants
 */

/**
 * @typedef {object} AiActivityProps
 * @property {AiActivityRecipeVariants['type']} type - Type of AI activity
 * @property {string} title - Title of the notification
 * @property {string | Date | number} timestamp - ISO date string, Date object, or Unix timestamp
 * @property {boolean} [defaultOpen=false] - Whether notification is open by default
 * @property {boolean} [isCollapsible=true] - Whether notification can be collapsed
 * @property {'normal' | 'sm'} [size='normal'] - Size of the activity content padding
 * @property {'success' | 'failure'} [status=''] - Status of the activity
 * @property {number} [openDuration=0.8] - Duration of the open animation
 * @property {number} [openBounce=0.3] - Bounce of the open animation
 * @property {number} [closeDuration=0.5] - Duration of the close animation
 * @property {number} [closeBounce=0.2] - Bounce of the close animation
 */

/**
 * @type {AiActivityProps}
 */
const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: value =>
      [
        'default',
        'gather',
        'action',
        'handover',
        'ai',
        'respond',
        'wait',
        'complete',
      ].includes(value),
  },
  title: {
    type: String,
    required: true,
  },
  timestamp: {
    type: [String],
    required: false,
    default: '',
  },
  defaultOpen: {
    type: Boolean,
    default: undefined,
  },
  isCollapsible: {
    type: Boolean,
    default: undefined,
  },
  size: {
    type: String,
    default: 'normal',
    validator: value => ['normal', 'sm'].includes(value),
  },
  status: {
    type: String,
    default: '',
  },
  openDuration: {
    type: Number,
    default: 0.5,
  },
  openBounce: {
    type: Number,
    default: 0.2,
  },
  closeDuration: {
    type: Number,
    default: 0.4,
  },
  closeBounce: {
    type: Number,
    default: 0.1,
  },
});

// Compute defaults based on type
const computedDefaultOpen = computed(() => {
  if (props.defaultOpen !== undefined) return props.defaultOpen;
  return props.type === 'handover';
});

const computedIsCollapsible = computed(() => {
  if (props.isCollapsible !== undefined) return props.isCollapsible;
  return props.type !== 'handover';
});

const isOpen = ref(computedDefaultOpen.value);

const iconMap = {
  gather: 'search',
  action: 'automation',
  handover: 'alert-triangle',
  ai: 'magic',
  respond: 'chat-ai',
  wait: 'clock',
  complete: 'check-circle',
};

const classes = computed(() =>
  aiActivityRecipe({
    type: props.type,
    size: props.size,
  })
);

function handleToggle() {
  if (computedIsCollapsible.value) {
    isOpen.value = !isOpen.value;
  }
}

// Title ellipsis detection for tooltip (lazy loaded on hover)
const titleEl = ref(null);
const isTitleEllipsis = ref(false);
const hasCheckedEllipsis = ref(false);

/**
 * Check if title text is truncated (lazy check on first hover)
 * Uses scrollWidth vs clientWidth for accurate overflow detection
 */
function checkTitleEllipsis() {
  if (!titleEl.value?.$el || hasCheckedEllipsis.value) return;

  const element = titleEl.value.$el;
  isTitleEllipsis.value = element.scrollWidth > element.clientWidth;
  hasCheckedEllipsis.value = true;
}
</script>

<template>
  <section :data-type="type" :class="classes.container()">
    <header
      v-cs-tooltip="isTitleEllipsis ? title : ''"
      :class="classes.header({ isOpen, isCollapsible: computedIsCollapsible })"
      @click="handleToggle"
    >
      <div class="flex min-w-0 flex-1 items-center gap-2">
        <template v-if="props.type === 'default'">
          <CsIcon
            v-if="props.status === 'success'"
            name="check-circle-filled"
            class="shrink-0 text-brand-green-11"
          />
          <CsIcon
            v-else-if="props.status === 'failure'"
            name="xcircle-filled"
            variant-color="critical"
            class="shrink-0"
          />
        </template>
        <CsIcon
          v-else
          :name="iconMap[type]"
          :variant-color="props.type === 'ai' ? 'magic' : 'icon'"
          :class="classes.icon()"
          class="shrink-0"
        />

        <CsText
          ref="titleEl"
          v-cs-tooltip="isTitleEllipsis ? title : ''"
          weight="semibold"
          variant="body-sm"
          :class="classes.title()"
          class="overflow-hidden text-ellipsis whitespace-nowrap"
          @mouseover="checkTitleEllipsis"
        >
          {{ title }}
        </CsText>

        <slot name="title-suffix" />
      </div>

      <div class="flex shrink-0 items-center gap-1">
        <slot name="header-suffix" />

        <CsText
          v-if="props.timestamp"
          variant-color="secondary"
          variant="body-sm"
        >
          {{ props.timestamp }}
        </CsText>

        <CsIcon
          v-if="computedIsCollapsible"
          name="chevron-right"
          variant-color="secondary"
          :class="classes.chevron({ isOpen })"
        />
      </div>
    </header>

    <div>
      <CsCollapse
        :is-open="isOpen"
        :open-duration="props.openDuration"
        :open-bounce="props.openBounce"
        :close-duration="props.closeDuration"
        :close-bounce="props.closeBounce"
      >
        <div
          class="flex flex-col gap-6 bg-brand-background"
          :class="size === 'sm' ? 'p-2' : 'p-3'"
        >
          <slot />
        </div>
      </CsCollapse>
    </div>
  </section>
</template>
